import React from 'react';

import { Chip } from '@mui/material';

export interface PolicyStatusChipsProps {
  isActive?: boolean;
  isCancelled?: boolean;
  isNonCompliant?: boolean;
  isControllingCoverage?: boolean;
  isIneligible?: boolean;
  isMultiState?: boolean;
  isMultiStatePolicy?: boolean;
}

type ChipConfig = {
  key: string;
  label: string;
  color: 'default' | 'success' | 'error' | 'info' | 'warning';
  testId: string;
  condition: (props: PolicyStatusChipsProps) => boolean;
  priority?: number; // For ordering
};

const CHIP_CONFIGS: ChipConfig[] = [
  // Primary status (highest priority)
  {
    key: 'cancelled',
    label: 'Cancelled',
    color: 'default',
    testId: 'policy-status-cancelled',
    condition: (props) => Boolean(props.isCancelled),
    priority: 1,
  },
  {
    key: 'active',
    label: 'Active',
    color: 'success',
    testId: 'policy-status-active',
    condition: (props) => Boolean(props.isActive && !props.isCancelled),
    priority: 1,
  },
  {
    key: 'expired',
    label: 'Expired',
    color: 'default',
    testId: 'policy-status-expired',
    condition: (props) => props.isActive === false && props.isCancelled === false,
    priority: 1,
  },
  // Secondary status indicators
  {
    key: 'noncompliant',
    label: 'Noncompliant',
    color: 'error',
    testId: 'policy-status-noncompliant',
    condition: (props) => Boolean(props.isNonCompliant),
  },
  {
    key: 'controlling',
    label: 'Controlling coverage',
    color: 'info',
    testId: 'policy-status-controlling',
    condition: (props) => Boolean(props.isControllingCoverage),
  },
  {
    key: 'ineligible',
    label: 'Ineligible',
    color: 'error',
    testId: 'policy-status-ineligible',
    condition: (props) => Boolean(props.isIneligible),
  },
  {
    key: 'multistate',
    label: 'Multi-state',
    color: 'warning',
    testId: 'policy-status-multistate',
    condition: (props) => Boolean(props.isMultiState),
  },
  {
    key: 'multistate-policy',
    label: 'Multi-state policy',
    color: 'info',
    testId: 'policy-status-multistate-policy',
    condition: (props) => Boolean(props.isMultiStatePolicy),
  },
] as const;

export function PolicyStatusChips(props: PolicyStatusChipsProps) {
  const applicableChips = CHIP_CONFIGS.filter((config) => config.condition(props)).sort((a, b) => {
    // Sort by priority if both have priority defined
    if (a.priority !== undefined && b.priority !== undefined) {
      return a.priority - b.priority;
    }
    // If only one has priority, prioritize it
    if (a.priority !== undefined && b.priority === undefined) return -1;
    if (a.priority === undefined && b.priority !== undefined) return 1;
    // If neither has priority, maintain original order
    return 0;
  });

  return (
    <>
      {applicableChips.map((config) => (
        <Chip
          key={config.key}
          sx={{ ml: 1 }}
          label={config.label}
          color={config.color}
          variant="soft"
          size="small"
          data-testid={config.testId}
        />
      ))}
    </>
  );
}

// Legacy function export for backward compatibility
export const renderPolicyStatusChips = (props: PolicyStatusChipsProps) => (
  <PolicyStatusChips {...props} />
);
